import * as React from 'react';
import {useTranslation} from 'react-i18next';
import {cn} from '../../lib/cn';
import {useCombinedRef} from '../../hooks';
import {FieldErrorMessage} from '../../field-error-message';
import {HelpTooltip} from '../../help-tooltip';
import {useTextareaValueState} from './useTextareaValueState';

type TextareaProps = Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'name'> & {
  label?: React.ReactNode;
  invalid?: boolean;
  error?: string;
  empty?: boolean;
  optional?: boolean | string;
  loading?: boolean;
  tooltip?: React.ReactNode;
  helperText?: string;
  name?: string;
  className?: string;
  textareaClassName?: string;
  minRows?: number;
  maxRows?: number;
  autosize?: boolean;
};

const LINE_HEIGHT = 20;
const VERTICAL_PADDING = 32;

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  {
    className,
    textareaClassName,
    label,
    invalid,
    error,
    empty,
    optional,
    disabled,
    readOnly,
    loading,
    tooltip,
    name,
    id,
    helperText,
    minRows = 3,
    maxRows,
    autosize = true,
    value,
    defaultValue,
    onInput,
    onChange,
    onFocus,
    onBlur,
    ...textareaProps
  },
  ref,
) {
  const {textareaRef, isControlled, isEmpty, setNativeValue, syncValueState} =
    useTextareaValueState({empty, value, defaultValue});
  const combinedRef = useCombinedRef(ref, textareaRef);
  const reactId = React.useId();
  const textareaId = id ?? name ?? `dash-textarea-${reactId}`;
  const {t} = useTranslation();

  const isInvalid = Boolean(invalid || error);
  const isBlocked = Boolean(disabled);

  const resize = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el || !autosize) return;
    el.style.height = 'auto';
    const minHeight = minRows * LINE_HEIGHT + VERTICAL_PADDING;
    const maxHeight = maxRows ? maxRows * LINE_HEIGHT + VERTICAL_PADDING : Infinity;
    const nextHeight = Math.min(Math.max(el.scrollHeight, minHeight), maxHeight);
    el.style.height = `${nextHeight}px`;
    el.style.overflowY = el.scrollHeight > nextHeight ? 'auto' : 'hidden';
  }, [autosize, maxRows, minRows, textareaRef]);

  React.useLayoutEffect(() => {
    resize();
  }, [resize, value]);

  const handleInput = (event: React.FormEvent<HTMLTextAreaElement>) => {
    resize();
    onInput?.(event);
  };

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (readOnly || disabled) return;
    if (!isControlled) {
      setNativeValue(event.currentTarget.value);
    }
    onChange?.(event);
  };

  const handleFocus = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    if (readOnly || disabled) return;
    onFocus?.(event);
    syncValueState();
  };

  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    onBlur?.(event);
    syncValueState();
  };

  return (
    <div
      className={cn(
        'relative block min-h-[88px] w-[var(--field-width,100%)] max-w-[var(--field-max-width,296px)]',
        isBlocked && 'cursor-not-allowed opacity-50',
        loading && 'cursor-progress opacity-50',
        className,
      )}
    >
      {label && (
        <label
          htmlFor={textareaId}
          className="mb-2 flex select-none items-center text-[length:var(--field-label-font-size)] font-[var(--field-label-font-weight)] text-[var(--chekin-color-brand-navy)]"
        >
          {label}
          {tooltip && (
            <span className="ml-1 inline-flex">
              <HelpTooltip content={tooltip} size={16} />
            </span>
          )}
        </label>
      )}
      <textarea
        ref={combinedRef}
        id={textareaId}
        name={name}
        value={value}
        defaultValue={defaultValue}
        rows={minRows}
        disabled={isBlocked || loading}
        readOnly={isBlocked || readOnly}
        aria-invalid={isInvalid}
        aria-busy={loading}
        onInput={handleInput}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cn(
          'm-0 box-border block w-full resize-none rounded-[6px] border bg-white px-4 py-4 text-[16px] font-medium leading-5 text-[var(--chekin-color-brand-navy)] outline-none transition-colors duration-100 ease-in-out',
          'border-[var(--field-border)] placeholder:text-[length:var(--field-placeholder-font-size)] placeholder:font-[var(--field-placeholder-font-weight)] placeholder:text-[var(--chekin-color-gray-1)]',
          !isBlocked && 'focus:border-[var(--field-border-focused)]',
          isEmpty &&
            'border-[var(--field-border)] bg-[var(--empty-field-background)] text-[var(--chekin-color-gray-1)]',
          isInvalid &&
            'border-[var(--field-border-invalid)] focus:border-[var(--field-border-invalid)]',
          (readOnly || isBlocked) && 'cursor-default',
          isBlocked && 'cursor-not-allowed',
          loading && 'cursor-progress',
          textareaClassName,
        )}
        style={
          autosize ? {minHeight: minRows * LINE_HEIGHT + VERTICAL_PADDING} : undefined
        }
        {...textareaProps}
      />
      {error && (
        <FieldErrorMessage
          id={`${textareaId}-error`}
          message={error}
          className="mt-[1px] text-[14px]"
        />
      )}
      {!error && optional && (
        <span className="mt-[1px] block text-left text-[14px] font-medium text-[var(--chekin-color-gray-1)]">
          {typeof optional === 'string' ? optional : t('optional')}
        </span>
      )}
      {!error && helperText && (
        <span className="mt-[1px] block text-[12px] font-normal text-[var(--chekin-color-gray-1)]">
          {helperText}
        </span>
      )}
    </div>
  );
});

export {Textarea};
export type {TextareaProps};
